import { BudgetResourceType } from 'helpers/userRights/types';
import { FINANCE_SCOPE } from '../config/constants';
import {
    BudgetBys,
    BudgetExpenseOrIncome,
    SCOPE_PERMISSION_WRITE,
} from 'business/constants';

type ArgumentKeysT = {
    budgetProposalId?: number;
    resourceSoftwareId?: string;
};
type BudgetLineKeysT = {
    expenseOrIncome?: string;
    investmentOperating?: string | null;
    exercice?: number;
    operationSoftwareId?: string;
    budgetName?: string;
    resourceSoftwareId?: string;
};
type BudgetProposalsKeysT = {
    withResources?: boolean;
    budgetProposalId?: number;
};
type CadrageKeysT = {
    exercice?: number | null;
    budgetProposalId?: number;
    budgetLineCode?: string;
    searchCustomCadrage?: boolean;
    searchSummary?: boolean;
};
type InitialisationKeysT = {
    initialisationId?: number;
    isAvailable?: boolean;
};
type InscriptionKeysT = {
    expenseOrIncome?: string;
    investmentOperating?: string | null;
    additionalFilters?: QueryFilterT[];
    customCacheKeySuffix?: string;
    exercice?: number;
    operationSoftwareId?: string;
    enrichedOperationSoftwareId?: string;
    secondaryBudgetAxisElementSoftwareId?: string;
    chapterSoftwareId?: string;
    budgetName?: string;
    budgetProposalIds?: number[];
    resourceSoftwareId?: string;
};
type MultiYearsComparisonQueryKeyT = {
    year: number;
    budgetName?: string | null;
    isReportedAmountUsed?: boolean;
};
type VentilatedBudgetProposalKeysT = {
    budgetProposalId: number;
    isOperation: boolean;
    isReportedAmountUsed?: boolean;
    budgetLineCode?: string;
};

type OperationsAmountsKeysT = {
    exercice: number;
    expenseOrIncome: BudgetExpenseOrIncome;
    budgetName: string;
};

type ProjectKeysT = {
    filters: QueryFilterT[];
    budget_by: BudgetBys;
};

type SecondaryBudgetAxisSourceKey = ['secondary_budget_axis_source'];

export type ArgumentQueryKey = [
    'arguments',
    number | undefined,
    string | undefined,
];
export type BudgetLineQueryKey = [
    'budget_lines',
    string | undefined,
    string | undefined | null,
    number | undefined,
    string | undefined,
    string | undefined,
    string | undefined,
];
export type BudgetQueryKey = ['budget'];
export type BudgetProposalsQueryKey = [
    'budget_proposals',
    boolean,
    number | undefined,
];
export type CadrageQueryKey = [
    'cadrage',
    number | null | undefined,
    number | undefined,
    string | undefined,
    boolean,
    boolean,
];
export type InitialisationQueryKey = [
    'initialisation',
    number | undefined,
    boolean | undefined,
];
export type InscriptionQueryKey = [
    'inscriptions',
    number | undefined,
    string | undefined | null,
    string | undefined,
    string | undefined,
    string | undefined,
    string | undefined,
    number[] | undefined,
    string | undefined,
    ...string[],
];
export type InscriptionOptionsQueryKey = ['inscription_options'];
export type MultiYearsComparisonQueryKey = [
    'multi_year_comparison',
    number,
    string | null | undefined,
    boolean | undefined,
];
export type VentilatedBudgetProposalQueryKey = [
    'budget_ventilated_data',
    number,
    boolean,
    boolean | undefined,
    string | undefined,
];

type OperationsAmountsKeys = [
    'operations_amounts',
    number,
    BudgetExpenseOrIncome,
    string | undefined,
];

type EditableValuesQueryKey = [
    'editable_values',
    BudgetResourceType,
    typeof FINANCE_SCOPE,
    typeof SCOPE_PERMISSION_WRITE,
];
export type ProjectQueryKey = ['project', QueryFilterT[], BudgetBys];

export const createBudgetQueryKeys = () => {
    const argumentKeys = ({
        budgetProposalId,
        resourceSoftwareId,
    }: ArgumentKeysT): ArgumentQueryKey => [
        'arguments',
        budgetProposalId,
        resourceSoftwareId,
    ];

    const budgetLinesKeys = ({
        expenseOrIncome = undefined,
        investmentOperating = undefined,
        exercice = undefined,
        operationSoftwareId = undefined,
        budgetName = undefined,
        resourceSoftwareId = undefined,
    }: BudgetLineKeysT = {}): BudgetLineQueryKey => [
        'budget_lines',
        expenseOrIncome,
        investmentOperating,
        exercice,
        operationSoftwareId,
        budgetName,
        resourceSoftwareId,
    ];

    const budgetProposalsKeys = ({
        withResources = false,
        budgetProposalId = undefined,
    }: BudgetProposalsKeysT = {}): BudgetProposalsQueryKey => [
        'budget_proposals',
        withResources,
        budgetProposalId,
    ];

    const budgetKeys = (): BudgetQueryKey => ['budget'];

    const cadrageKeys = ({
        exercice = undefined,
        budgetProposalId = undefined,
        budgetLineCode = undefined,
        searchCustomCadrage = false,
        searchSummary = false,
    }: CadrageKeysT = {}): CadrageQueryKey => [
        'cadrage',
        exercice,
        budgetProposalId,
        budgetLineCode,
        searchCustomCadrage,
        searchSummary,
    ];

    const initialisationKeys = ({
        initialisationId = undefined,
        isAvailable = undefined,
    }: InitialisationKeysT = {}): InitialisationQueryKey => [
        'initialisation',
        initialisationId,
        isAvailable,
    ];

    const inscriptionsKeys = ({
        expenseOrIncome = undefined,
        investmentOperating = undefined,
        additionalFilters = undefined,
        customCacheKeySuffix = '',
        exercice = undefined,
        operationSoftwareId = undefined,
        enrichedOperationSoftwareId = undefined,
        secondaryBudgetAxisElementSoftwareId = undefined,
        chapterSoftwareId = undefined,
        budgetName = undefined,
        budgetProposalIds = undefined,
        resourceSoftwareId = undefined,
    }: InscriptionKeysT = {}): InscriptionQueryKey => {
        const key: InscriptionQueryKey = [
            'inscriptions',
            exercice,
            operationSoftwareId,
            enrichedOperationSoftwareId,
            secondaryBudgetAxisElementSoftwareId,
            chapterSoftwareId,
            budgetName,
            budgetProposalIds,
            resourceSoftwareId,
        ];
        if (expenseOrIncome) {
            key.push(expenseOrIncome.toUpperCase());
        }
        if (investmentOperating) {
            key.push(investmentOperating.toUpperCase());
        }
        if (additionalFilters) {
            key.push(JSON.stringify(additionalFilters));
        }
        if (customCacheKeySuffix) {
            key.push(customCacheKeySuffix);
        }
        return key;
    };

    const inscriptionOptionsKeys = (): InscriptionOptionsQueryKey => [
        'inscription_options',
    ];

    const multiYearsComparisonKeys = ({
        year,
        budgetName = undefined,
        isReportedAmountUsed = false,
    }: MultiYearsComparisonQueryKeyT): MultiYearsComparisonQueryKey => [
        'multi_year_comparison',
        year,
        budgetName,
        isReportedAmountUsed,
    ];

    const ventilatedBudgetProposalKeys = ({
        budgetProposalId,
        isOperation,
        isReportedAmountUsed = false,
        budgetLineCode = undefined,
    }: VentilatedBudgetProposalKeysT): VentilatedBudgetProposalQueryKey => [
        'budget_ventilated_data',
        budgetProposalId,
        isOperation,
        isReportedAmountUsed,
        budgetLineCode,
    ];

    const operationsAmountsKeys = ({
        exercice,
        expenseOrIncome,
        budgetName,
    }: OperationsAmountsKeysT): OperationsAmountsKeys => [
        'operations_amounts',
        exercice,
        expenseOrIncome,
        budgetName,
    ];

    const writeScopesQueryKeys = (
        budgetResource: BudgetResourceType,
    ): EditableValuesQueryKey => [
        'editable_values',
        budgetResource,
        FINANCE_SCOPE,
        SCOPE_PERMISSION_WRITE,
    ];

    const projectKeys = ({
        filters,
        budget_by,
    }: ProjectKeysT): ProjectQueryKey => ['project', filters, budget_by];

    const secondaryBudgetAxisSourceKeys = (): SecondaryBudgetAxisSourceKey => [
        'secondary_budget_axis_source',
    ];
    return {
        argumentKeys,
        budgetKeys,
        budgetLinesKeys,
        budgetProposalsKeys,
        cadrageKeys,
        initialisationKeys,
        inscriptionOptionsKeys,
        inscriptionsKeys,
        multiYearsComparisonKeys,
        operationsAmountsKeys,
        projectKeys,
        secondaryBudgetAxisSourceKeys,
        ventilatedBudgetProposalKeys,
        writeScopesQueryKeys,
    };
};
